import React, { useMemo, useState } from 'react';
import EmptyState from '../atoms/EmptyState';
import {
  attachmentValues,
  resolveIsAttachmentField,
} from '../../utils/complaintTable';

const fallbackStatusNames = ['Open', 'Closed'];
const fallbackEscalationLabels = ['Level 1', 'Level 2', 'Level 3'];
const IMAGE_FILE_NAME = /\.(png|jpe?g|gif|webp|bmp|svg)(?:\?.*)?$/i;

function S3AttachmentCell({ value, urlMap = {} }) {
  const attachments = useMemo(() => attachmentValues(value), [value]);
  const [previewIndex, setPreviewIndex] = useState(null);

  if (!attachments.length) return '-';

  const imageAttachments = attachments.filter((attachment) => (
    typeof attachment === 'string' && IMAGE_FILE_NAME.test(attachment)
  ));
  const fileAttachments = attachments
    .filter((attachment) => typeof attachment === 'string' && !IMAGE_FILE_NAME.test(attachment))
    .map((attachment) => ({ name: attachment, url: urlMap[attachment] }));
  const previewImages = imageAttachments
    .map((attachment) => ({ name: attachment, url: urlMap[attachment] }))
    .filter((attachment) => attachment.url);
  const firstImage = previewImages[0];
  const closePreview = () => setPreviewIndex(null);
  const showPrevious = () => setPreviewIndex((current) => (
    current > 0 ? current - 1 : previewImages.length - 1
  ));
  const showNext = () => setPreviewIndex((current) => (
    current < previewImages.length - 1 ? current + 1 : 0
  ));

  return (
    <>
      <div className="ca-complaint-table__attachments">
  {firstImage ? (
    <button
      type="button"
      className="ca-complaint-table__attachment-trigger"
      onClick={() => setPreviewIndex(0)}
      aria-label={`Preview ${firstImage.name}`}
    >
      <img
        className="ca-complaint-table__attachment-image"
        src={firstImage.url}
        alt={firstImage.name}
        title={firstImage.name}
      />
    </button>
  ) : null}

  {fileAttachments.length
    ? fileAttachments.map((attachment) => (
        attachment.url ? (
          <a
            key={attachment.name}
            className="ca-complaint-table__attachment-link"
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
          >
            {attachment.name.split('/').pop()}
          </a>
        ) : (
          <span
            key={attachment.name}
            className="ca-complaint-table__attachment-name"
          >
            {attachment.name}
          </span>
        )
      ))
    : null}

  {!firstImage && !fileAttachments.length
    ? attachments.map((attachment) => (
        <span
          key={String(attachment)}
          className="ca-complaint-table__attachment-name"
        >
          {String(attachment)}
        </span>
      ))
    : null}

  {previewImages.length > 1 ? (
    <button
      type="button"
      className="ca-complaint-table__attachment-count"
      onClick={() => setPreviewIndex(1)}
      aria-label={`Preview ${previewImages.length - 1} more images`}
    >
      +{previewImages.length - 1}
    </button>
  ) : null}
</div>
      {previewIndex != null && previewImages.length ? (
        <div className="ca-complaint-table__preview" role="dialog" aria-modal="true" aria-label="Image preview">
          <button type="button" className="ca-complaint-table__preview-close" onClick={closePreview} aria-label="Close image preview">×</button>
          <button type="button" className="ca-complaint-table__preview-nav ca-complaint-table__preview-nav--previous" onClick={showPrevious} aria-label="Previous image">‹</button>
          <img className="ca-complaint-table__preview-image" src={previewImages[previewIndex].url} alt={previewImages[previewIndex].name} />
          <button type="button" className="ca-complaint-table__preview-nav ca-complaint-table__preview-nav--next" onClick={showNext} aria-label="Next image">›</button>
          <span className="ca-complaint-table__preview-count">{previewIndex + 1} / {previewImages.length}</span>
        </div>
      ) : null}
    </>
  );
}

function ComplaintTableSelect({ value, options, fallbackOptions, ariaLabel, selectKey, onChange, disabled = false }) {
  const source = options.length ? options : fallbackOptions.map((name) => ({ value: name, label: name }));
  const list = source.map((item) => {
    if (item && typeof item === 'object') {
      return {
        value: String(item.value ?? item.id ?? ''),
        label: item.label ?? item.name ?? String(item.value ?? item.id ?? ''),
        disabled: Boolean(item.disabled),
      };
    }
    return { value: String(item), label: String(item), disabled: false };
  }).filter((item) => item.value || item.label);
  const selected = value != null && value !== '' && value !== '-' ? String(value) : '';
  const values = list.map((item) => item.value);
  const withSelected = selected && !values.includes(selected)
    ? [{ value: selected, label: selected, disabled: false }, ...list]
    : list;
  const shown = withSelected.some((item) => item.value === selected)
    ? selected
    : (withSelected[0]?.value ?? '');

  return (
        <select
      className="ca-complaint-table__select"
      aria-label={ariaLabel}
      value={shown}
      disabled={disabled}
      title={disabled ? 'You do not have permission to change this' : undefined}
      onChange={(event) => onChange?.(event.target.value)}
      key={selectKey}
    >
      {withSelected.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function dropdownContainsValue(options, value) {
  if (value == null || value === '' || !Array.isArray(options)) return false;
  return options.some((item) => (
    item && typeof item === 'object'
      ? String(item.value ?? item.id) === String(value)
      : String(item) === String(value)
  ));
}

function getComplaintCellClassName(key, isHeader = false) {
  const classes = [];
  if (!isHeader && key === 'description') classes.push('ca-complaint-table__desc');
  if (key === 'status') classes.push('ca-complaint-table__sticky', 'ca-complaint-table__sticky--status');
  if (key === 'escalationTo') classes.push('ca-complaint-table__sticky', 'ca-complaint-table__sticky--escalation');
  return classes.length ? classes.join(' ') : undefined;
}

function renderComplaintTableCell(field, row, statusNames, escalationLabels, ctx = {}) {
  const value = row[field.key] ?? '-';
  const statusOptionsList = ctx.statusOptions ?? statusNames;
  const escalationOptionsList = ctx.escalationOptions ?? escalationLabels;

  if (field.key === 'status') {
    return (
      <ComplaintTableSelect
        value={dropdownContainsValue(statusOptionsList, row.statusId) ? row.statusId : value}
        options={statusOptionsList}
        fallbackOptions={fallbackStatusNames}
        ariaLabel="Status"
        disabled={ctx.canChangeStatus === false}
        selectKey={`status-${row.id}`}
        onChange={ctx.onStatusChange}
      />
    );
  }

  if (field.key === 'escalationTo') {
    return (
      <ComplaintTableSelect
        value={dropdownContainsValue(escalationOptionsList, row.currentStepId) ? row.currentStepId : value}
        options={escalationOptionsList}
        fallbackOptions={fallbackEscalationLabels}
        ariaLabel="Escalation to"
        disabled={ctx.canChangeEscalation === false}
        selectKey={`escalation-${row.id}`}
        onChange={ctx.onEscalationChange}
      />
    );
  }

  if (resolveIsAttachmentField(field, value) && attachmentValues(value).some((attachment) => typeof attachment === 'string')) {
    return <S3AttachmentCell value={value} urlMap={ctx.attachmentUrlMap} />;
  }

  return value;
}

export default function ComplaintTableGrid({
  scrollFields,
  stickyFields,
  tableRows,
  fieldLabel,
  statusNames,
  getCellContext,
  page,
  itemsPerPage,
}) {
  return (
    <div className="ca-complaint-table__scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">#</th>
            {scrollFields.map((field) => (
              <th scope="col" key={field.id} className={getComplaintCellClassName(field.key, true)}>{fieldLabel(field)}</th>
            ))}
            <th className="ca-complaint-table__spacer" aria-hidden="true" />
            {stickyFields.map((field) => (
              <th scope="col" key={field.id} className={getComplaintCellClassName(field.key, true)}>{fieldLabel(field)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row, index) => (
            <tr key={row.id ?? index}>
              <td>{((page - 1) * itemsPerPage) + index + 1}</td>
              {scrollFields.map((field) => (
                <td key={`${row.id ?? index}-${field.id}`} className={getComplaintCellClassName(field.key)}>
                  {renderComplaintTableCell(field, row, statusNames, fallbackEscalationLabels, getCellContext(row))}
                </td>
              ))}
              <td className="ca-complaint-table__spacer" aria-hidden="true" />
              {stickyFields.map((field) => (
                <td key={`${row.id ?? index}-${field.id}`} className={getComplaintCellClassName(field.key)}>
                  {renderComplaintTableCell(field, row, statusNames, fallbackEscalationLabels, getCellContext(row))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!tableRows.length ? (
        <div className="ca-complaint-table__empty-overlay">
          <EmptyState text="No Complaint" />
        </div>
      ) : null}
    </div>
  );
}
